<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <AdminPageHeader
      title="Concern Types"
      subtitle="Manage appointment concern type categories"
      icon="fas fa-tags"
      gradient="blue"
    />

    <BRow class="py-2">
      <BCol
        cols="12"
        md="6"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="tableData.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Status"
          label-for="filterActive"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableData.selected.active"
            input-id="filterActive"
            label="name"
            :options="activeOptions"
            clearable
            append-to-body
            :calculate-position="calculateDropPosition"
            :disabled="tableData.busy"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Action"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BButton
            size="sm"
            type="button"
            variant="primary"

            @click.prevent="createItem"
          >
            Add Concern Type
          </BButton>
        </BFormGroup>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableData"
      :provider="tableProvider"
      :fields="tableData.fields"
      :filter="tableData.filter"
      :filter-on="tableData.filterOn"
      :sort-by="tableData.sortBy"
      :sort-desc="tableData.sortDesc"
      :sort-direction="tableData.sortDirection"
      :total-rows="tableData.totalRows"
      :current-page="tableData.currentPage"
      :per-page="tableData.perPage"
      :stacked="tableData.stacked"
      :busy="tableData.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-2">
          <BButton
            size="sm"
            variant="primary"
            :disabled="tableData.busy"
            @click.prevent="editItem(item)"
          >
            Edit
          </BButton>
          <BButton
            size="sm"
            variant="danger"
            :disabled="tableData.busy"
            @click.prevent="deleteItemConfirm(item)"
          >
            Delete
          </BButton>
        </div>
      </template>

      <template #cell(active)="{ item }">
        <BBadge :variant="item.active ? 'success' : 'secondary'">
          {{ item.active ? 'Active' : 'Inactive' }}
        </BBadge>
      </template>
    </CustomTable>

    <BModal
      ref="modalForm"
      size="lg"
      scrollable
      centered
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      :title="state.editing ? 'Edit Concern Type' : 'Add Concern Type'"
    >
      <ValidationObserver ref="formData">
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>
            <BCol cols="12">
              <ValidationProvider
                v-slot="{ errors }"
                vid="name"
                name="name"
                rules="required|max:255"
              >
                <BFormGroup
                  label="Name"
                  label-for="fName"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fName"
                    v-model="formData.name"
                    placeholder="enter name here"
                    maxlength="255"
                    autocomplete="off"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol cols="12">
              <ValidationProvider
                v-slot="{ errors }"
                vid="description"
                name="description"
                rules="max:255"
              >
                <BFormGroup
                  label="Description"
                  label-for="fDescription"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormTextarea
                    id="fDescription"
                    v-model="formData.description"
                    placeholder="enter description here"
                    rows="3"
                    maxlength="255"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              v-if="state.editing"
              cols="12"
            >
              <BFormGroup class="mb-2">
                <BFormCheckbox
                  id="is_active"
                  v-model="formData.active"
                  switch
                  value="1"
                  name="is_active"
                  unchecked-value="0"
                  :disabled="state.busy"
                >
                  Is Active?
                </BFormCheckbox>
              </BFormGroup>
            </BCol>
          </BRow>
        </form>
      </ValidationObserver>

      <template #modal-footer="{ cancel }">
        <BButton
          variant="primary"
          :disabled="state.busy"
          @click.prevent="validateForm"
        >
          {{ state.editing ? 'Update' : 'Create' }}
        </BButton>
        <BButton
          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>
    </BModal>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'

import AdminPageHeader from '@/components/core/AdminPageHeader.vue'
import { SAdminAppointmentConcernType } from '@/services'

export default {
  name: 'VAdminAppointmentConcernTypes',

  components: { AdminPageHeader },

  mixins: [MxMisc, MxFormatter],

  middleware: ['auth', 'admin'],

  metaInfo: () => ({ title: 'Concern Types' }),

  data () {
    return {
      state: { busy: false, editing: false },
      activeOptions: [
        { id: 1, name: 'Active' },
        { id: 0, name: 'Inactive' }
      ],
      formData: {
        id: '', name: '', description: '', active: 1
      },
      tableData: {
        busy: false,
        selected: {
          active: null
        },
        filter: {
          search: '',
          active: ''
        },
        filterOn: [],
        stacked: this.isMobile(),
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 12, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 1, key: 'name', class: 'min-w-150px', sortable: true },
          { mobile: 2, key: 'description', class: 'min-w-250px' },
          { mobile: 3, key: 'active', class: 'text-center w-md-75px' },
          { mobile: 4, key: 'updated_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: mapGetters({ currentUser: 'auth/user' }),

  watch: {
    'tableData.selected.active' (active) {
      this.tableData.filter.active = active !== null && active !== undefined ? active.id : ''
    }
  },

  methods: {
    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableData.busy = true
      return await SAdminAppointmentConcernType.getList({
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        active: filter.active
      }).then(({ data }) => {
        this.tableData.totalRows = data.total_rows
        return data.items
      }).catch(() => { this.tableData.totalRows = 0; return [] }).finally(() => { this.tableData.busy = false })
    },

    createItem () {
      this.state.editing = false
      this.formData = { id: '', name: '', description: '', active: 1 }
      this.$refs.modalForm.show()
    },

    editItem (item) {
      this.state.editing = true
      this.formData = { id: item.id, name: item.name, description: item.description, active: item.active }
      this.$refs.modalForm.show()
    },

    async validateForm () {
      await this.$refs.formData.validate().then(async valid => {
        if (!valid) return this.swalInvalid()
        this.swalConfirm({
          html: this.state.editing ? '<h6>Update this Concern Type?</h6>' : '<h6>Create this Concern Type?</h6>',
          confirmButtonText: this.state.editing ? 'Update' : 'Create',
          preConfirm: async () => {
            this.state.busy = true
            const op = this.state.editing
              ? SAdminAppointmentConcernType.update(this.formData.id, this.formData)
              : SAdminAppointmentConcernType.store(this.formData)
            return op.then(({ data: { message } }) => {
              this.swalSuccess(message).then(() => { this.$refs.modalForm.hide(); this.$refs.tableData.refetch() })
            }).catch(({ message }) => {
              if (message?.errors) this.$refs.formData.setErrors(message.errors)
            }).finally(() => { this.state.busy = false })
          }
        })
      })
    },

    async deleteItemConfirm (item) {
      const result = await this.$swal({ title: 'Delete Concern Type', text: `Delete "${item.name}"?`, icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Yes, delete it!' })
      if (result.isConfirmed) {
        this.state.busy = true
        try {
          await SAdminAppointmentConcernType.destroy(item.id)
          this.swalSuccess('Concern type deleted successfully')
          this.$refs.tableData.refetch()
        } catch (error) {
          this.swalError(error.response?.data?.message || 'Failed to delete')
        } finally { this.state.busy = false }
      }
    }
  }
}
</script>
